import React, { useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { Website, WebsiteStoreIdentity } from '../types';

export const storeIdentityGroups = [
  { title: 'Production iOS', fields: [
    ['iosAppStoreOwner', 'App Store owner'], ['iosAppleTeamId', 'Apple team ID'],
    ['iosBundleIdentifier', 'iOS bundle identifier'], ['iosAscAppId', 'App Store Connect app ID'],
    ['iosAppStoreUrl', 'App Store URL'], ['iosTestFlightUrl', 'TestFlight URL'],
  ] },
  { title: 'Production Android', fields: [
    ['androidPlayStoreOwner', 'Google Play owner'], ['androidPlayDeveloperAccountId', 'Google Play developer account ID'],
    ['androidPlayAppId', 'Google Play app ID'], ['androidPackageName', 'Android package name'],
    ['androidPlayStoreUrl', 'Google Play URL'], ['androidInternalTestingUrl', 'Android internal testing URL'],
  ] },
] as const;

type Field = keyof WebsiteStoreIdentity;
const fields = storeIdentityGroups.flatMap(group => [...group.fields]);
const inputValue = (website: Website, field: Field) => website[field] ?? '';
const safeLink = (value: string) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch { return false; }
};
const patternFor = (field: Field): string | undefined => {
  if (field === 'iosAppleTeamId') return '[A-Z0-9]{10}';
  if (['iosAscAppId', 'androidPlayDeveloperAccountId', 'androidPlayAppId'].includes(field)) return '[0-9]+';
  if (field.endsWith('Url')) return 'https://.*';
  return undefined;
};

export default function ProductionStoreIdentity({ website, onSave }: {
  website: Website;
  onSave?: (id: number, values: WebsiteStoreIdentity) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [values, setValues] = useState<WebsiteStoreIdentity>({});

  const startEditing = () => {
    setValues(Object.fromEntries(fields.map(([field]) => [field, inputValue(website, field)])));
    setError('');
    setEditing(true);
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!onSave || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = Object.fromEntries(fields.map(([field]) => [field, values[field]?.trim() || null]));
      await onSave(website.id!, payload);
      setEditing(false);
    } catch {
      setError('Store details could not be saved. Check the values and try again.');
    } finally { setSaving(false); }
  };

  return <section aria-label={`Production store details for ${website.name}`} className="mt-4">
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {editing ? <Form onSubmit={save}>
      {storeIdentityGroups.map(group => <fieldset key={group.title} className="mb-3" disabled={saving}>
        <legend className="h6">{group.title}</legend>
        <Row className="g-3">{group.fields.map(([field, label]) => <Col md={6} key={field}>
          <Form.Group controlId={`store-${website.id}-${field}`}>
            <Form.Label>{label}</Form.Label>
            <Form.Control name={field} type={field.endsWith('Url') ? 'url' : 'text'}
              pattern={patternFor(field)} maxLength={field.endsWith('Url') ? 2048 : 255}
              value={values[field] ?? ''} onChange={event => setValues(previous => ({ ...previous, [field]: event.target.value }))} />
          </Form.Group>
        </Col>)}</Row>
      </fieldset>)}
      <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save store details'}</Button>{' '}
      <Button variant="secondary" disabled={saving} onClick={() => { setEditing(false); setError(''); }}>Cancel</Button>
    </Form> : <>
      {storeIdentityGroups.map(group => <details key={group.title} className="mb-3">
        <summary className="fw-semibold">{group.title}</summary>
        <dl className="mt-2">{group.fields.map(([field, label]) => {
          const value = inputValue(website, field);
          return <div key={field} className="mb-2"><dt>{label}</dt><dd className="text-break mb-0">
            {value && field.endsWith('Url') && safeLink(value)
              ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>
              : value || 'Not set'}
          </dd></div>;
        })}</dl>
      </details>)}
      {onSave && <Button size="sm" variant="outline-secondary" onClick={startEditing}>Edit production store details</Button>}
    </>}
  </section>;
}
